import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

const SCROLL_OFFSET_PX = 10;
const SCROLL_DETECTION_TOLERANCE_PX = 12;

export function getOrderedSections(visibleCategories, sectionById) {
  const seen = new Set();
  const ordered = [];

  visibleCategories.forEach((category) => {
    category.sectionIds.forEach((sectionId) => {
      if (seen.has(sectionId)) {
        return;
      }
      const section = sectionById.get(sectionId);
      if (section) {
        seen.add(sectionId);
        ordered.push(section);
      }
    });
  });

  return ordered;
}

function buildSectionToCategoryId(visibleCategories) {
  const map = new Map();
  visibleCategories.forEach((category) => {
    category.sectionIds.forEach((sectionId) => {
      if (!map.has(sectionId)) {
        map.set(sectionId, category.id);
      }
    });
  });
  return map;
}

/** Última seção cujo topo já passou pela âncora do container (mesma regra do layout antigo). */
function findActiveSectionId(orderedSectionIds, anchorTop) {
  let bestSectionId = orderedSectionIds[0] ?? null;
  let bestTop = -Infinity;

  orderedSectionIds.forEach((sectionId) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (!element) {
      return;
    }

    const top = element.getBoundingClientRect().top;
    if (top <= anchorTop + SCROLL_DETECTION_TOLERANCE_PX && top > bestTop) {
      bestTop = top;
      bestSectionId = sectionId;
    }
  });

  return bestSectionId;
}

export default function useModalFormCategoryAnchor({
  mainRef,
  visibleCategories,
  sectionById,
  orderedSectionIds,
  layoutSyncKey,
}) {
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const ignoreNextAutoDetectRef = useRef(false);
  const frameRef = useRef(null);

  const resolvedCategoryId =
    activeCategoryId && visibleCategories.some((c) => c.id === activeCategoryId)
      ? activeCategoryId
      : (visibleCategories[0]?.id ?? null);

  const sectionToCategoryId = useMemo(
    () => buildSectionToCategoryId(visibleCategories),
    [visibleCategories]
  );

  const scrollToSection = useCallback(
    (sectionId) => {
      const scrollContainer = mainRef.current;
      const element = document.getElementById(`section-${sectionId}`);
      if (!scrollContainer || !element) {
        return;
      }

      const containerTop = scrollContainer.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const scrollPosition =
        scrollContainer.scrollTop + (elementTop - containerTop) - SCROLL_OFFSET_PX;

      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    },
    [mainRef]
  );

  const scrollToCategory = useCallback(
    (categoryId) => {
      const category = visibleCategories.find((c) => c.id === categoryId);
      const firstSectionId =
        category?.sectionIds.find((id) => sectionById.has(id)) ?? null;

      if (!firstSectionId) {
        return;
      }

      ignoreNextAutoDetectRef.current = true;
      setActiveCategoryId(categoryId);
      window.requestAnimationFrame(() => scrollToSection(firstSectionId));
    },
    [visibleCategories, sectionById, scrollToSection]
  );

  useEffect(() => {
    if (!orderedSectionIds.length) {
      return undefined;
    }

    const scrollContainer = mainRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const detectActiveCategory = () => {
      const anchorTop =
        scrollContainer.getBoundingClientRect().top + SCROLL_OFFSET_PX;
      const atBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - SCROLL_DETECTION_TOLERANCE_PX;
      const bestSectionId = atBottom
        ? orderedSectionIds[orderedSectionIds.length - 1]
        : findActiveSectionId(orderedSectionIds, anchorTop);
      const categoryId = bestSectionId
        ? sectionToCategoryId.get(bestSectionId)
        : null;

      if (!categoryId) {
        return;
      }

      setActiveCategoryId((prev) => (prev === categoryId ? prev : categoryId));
    };

    const scheduleDetect = () => {
      if (frameRef.current) {
        return;
      }
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        if (ignoreNextAutoDetectRef.current) {
          ignoreNextAutoDetectRef.current = false;
          return;
        }
        detectActiveCategory();
      });
    };

    scrollContainer.addEventListener('scroll', scheduleDetect, { passive: true });
    window.addEventListener('resize', scheduleDetect);

    return () => {
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      scrollContainer.removeEventListener('scroll', scheduleDetect);
      window.removeEventListener('resize', scheduleDetect);
    };
  }, [mainRef, orderedSectionIds, sectionToCategoryId, layoutSyncKey]);

  return { resolvedCategoryId, scrollToCategory };
}
